<template>
  <Segmented
    v-bind="$attrs"
    v-model:value="state"
    :class="looseMode ? 'items-loose' : ''"
    :options="getOptions"
    @change="handleChange"
  >
    <template v-for="item in Object.keys($slots)" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>
  </Segmented>
</template>

<script lang="ts" setup>
  import { Segmented } from 'ant-design-vue';
  import { get } from 'lodash-es';
  import { computed, PropType, ref, shallowRef, unref, useAttrs, watch } from 'vue';
  import type { Fn, Recordable } from '@mts-fe/types';
  import type { SegmentedOption, SegmentedValue } from 'ant-design-vue/es/segmented/src/segmented';
  import { useRuleFormItem } from '@mts-fe/hooks';
  import { isArray, isEqualByStringify, isFunction, propTypes } from '@mts-fe/utils';
  import { t } from '@mts/hooks/web/useI18n';
  import { useCountMap } from '../hooks/useCountMap';

  defineOptions({ name: 'ApiSegmented', inheritAttrs: false });

  type Option = SegmentedOption;

  const props = defineProps({
    value: { type: [String, Number] as PropType<SegmentedValue>, default: '' },
    numberToString: propTypes.bool.def(true),
    useFirstValue: propTypes.bool,
    api: {
      type: Function as PropType<(arg?: any) => Promise<Option[] | Recordable>>,
      default: null,
    },
    // api params
    params: propTypes.any.def({}),
    // support xxx.xxx.xx
    resultField: propTypes.string.def(''),
    labelField: propTypes.string.def('label'),
    valueField: propTypes.string.def('value'),
    immediate: propTypes.bool.def(true),
    prependAll: propTypes.bool.def(false),
    looseMode: propTypes.bool.def(false),
    showColor: propTypes.bool.def(false),
    countApi: propTypes.string,
    options: {
      type: Array<Option>,
      default: [],
    },
    beforeFetch: {
      type: Function as PropType<Fn>,
      default: null,
    },
    afterFetch: {
      type: Function as PropType<Fn>,
      default: null,
    },
  });

  const attrs = useAttrs();
  const emit = defineEmits(['options-change', 'change', 'update:value']);

  const optionsRef = shallowRef<Option[]>(props.options);
  const loading = ref(false);
  const emitData = shallowRef<Option[]>([]);

  // Embedded in the form, just use the hook binding to perform form verification
  const [state] = useRuleFormItem(props, 'value', 'change', emitData);

  const getOptions = computed(() => {
    const { labelField, valueField, numberToString, showColor } = props;

    return unref(optionsRef).reduce((prev, next) => {
      if (next) {
        const value = get(next, valueField);
        const className = showColor && next['color'] ? 'ant-segmented-' + next['color'] : void 0;
        prev.push({
          ...next,
          className,
          label: (get(next, labelField) || value) + getCountNum(value),
          value: numberToString ? `${value}` : value,
        });
      }
      return prev;
    }, [] as Option[]);
  });

  const { getCountNum } = useCountMap(props.countApi, getOptions, attrs.codeField as string);

  watch(
    () => state.value,
    () => {
      emit('update:value', state.value);
    },
  );

  watch(
    () => props.params,
    (value, oldValue) => {
      if (isEqualByStringify(value, oldValue)) return;
      fetch();
    },
    { deep: true, immediate: props.immediate },
  );

  async function fetch() {
    let { api, beforeFetch, afterFetch, params, resultField } = props;
    if (!api || !isFunction(api) || loading.value) return;
    optionsRef.value = [];
    try {
      loading.value = true;
      if (beforeFetch && isFunction(beforeFetch)) {
        const res = await beforeFetch(params);
        if (typeof res === 'boolean' && !res) return;
        params = res || params;
      }
      let res = await api(params);
      if (afterFetch && isFunction(afterFetch)) {
        res = (await afterFetch(res)) || res;
      }
      if (isArray(res)) {
        optionsRef.value = [...res];
        emitChange();
        return;
      }
      if (resultField) {
        optionsRef.value = get(res, resultField) || [];
      }
      emitChange();
    } catch (error) {
      console.warn(error);
      // reset status
    } finally {
      loading.value = false;
    }
  }

  function emitChange() {
    props.prependAll && optionsRef.value.unshift({ value: '', label: t('g.25090009') });
    emit('options-change', unref(getOptions));

    const first = unref(getOptions)[0];
    if (props.useFirstValue && !state.value && first) {
      state.value = first.value;
      handleChange(state.value, first);
    }
  }

  function handleChange(_, ...args) {
    emitData.value = args;
  }
</script>

<style lang="less">
  @import (reference) '@mts/design/color.less';

  .ant-segmented {
    each(@mts-color-map, { 
      .ant-segmented-@{key} > .ant-segmented-item-label{ 
        color: @value !important;
      }
    });
  }

  .ant-segmented.items-loose .ant-segmented-item-label {
    padding: 0 20px;
  }
</style>
